@layer refine.base {
  :host {
    --_dropdown-bg: var(--combobox-dropdown-bg, var(--color-canvas));
    --_dropdown-border-color: var(--combobox-dropdown-border-color, var(--color-contrast-200));
    --_option-hover-bg: var(--combobox-option-hover-bg, var(--color-contrast-100));
    --_option-focus-bg: var(
      --combobox-option-focus-bg,
      color-mix(in oklch, var(--_theme-base) 12%, var(--color-contrast-100))
    );
    --_option-selected-bg: var(
      --combobox-option-selected-bg,
      color-mix(in oklch, var(--_theme-base) 10%, var(--color-contrast-50))
    );
    --_option-selected-focus-bg: var(
      --combobox-option-selected-focus-bg,
      color-mix(in oklch, var(--_theme-base) 20%, var(--color-contrast-100))
    );

    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
    min-width: var(--combobox-min-width, 12rem);
  }

  :host([fullwidth]) {
    display: block;
    width: 100%;
  }

  /* .sr-only is provided by the shared srOnlyMixin (see styles: [...] in combobox.ts) */

  /* ore-input cursor override so the field still feels like a text input */
  .trigger::part(input) {
    cursor: text;
  }

  /* Allow the field to grow vertically when chips wrap to multiple rows. */
  .trigger::part(field) {
    height: auto;
    min-height: var(--input-height, var(--size-10));
  }

  /* Wrap chips + input across rows; suffix stays on same row as input.
     Only row-gap is set here — column spacing between chips is handled
     via margin on ore-chip so the empty prefix slot adds no extra offset
     (which would shift the input rightward vs the inset label). */
  .trigger::part(input-row) {
    flex-wrap: wrap;
    row-gap: var(--size-1);
    align-items: center;
  }

  /* ========================================
       Chips (prefix slot)
       ======================================== */

  /* display:contents removes chips-row from the flex layout so chips
     become direct flex items of input-row — no extra gap before input
     when there are no chips (fixes inset-label alignment). */
  .chips-row {
    display: contents;
  }

  /* chips-list is also display:contents so individual ore-chip elements
     are the direct flex items, wrapping inline with the search input. */
  .chips-list {
    display: contents;
  }

  /* Trailing space after each chip covers both inter-chip spacing and
     the gap between the last chip and the search input. */
  ore-chip {
    margin-inline-end: var(--size-1);
  }

  /* ========================================
       Suffix: clear button + loader + chevron
       ======================================== */

  .combobox-suffix {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-1);
    align-items: center;
  }

  .clear-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    padding: 0;
    color: var(--color-contrast-400);
    cursor: pointer;
    background: none;
    border: 0;
  }

  .clear-btn[hidden] {
    display: none;
  }

  .clear-btn:hover {
    color: var(--color-contrast-600);
  }

  .combobox-suffix-end {
    display: flex;
    gap: var(--size-1);
    align-items: center;
    color: var(--color-contrast-500);
  }

  .chevron {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    transition: var(--_motion-transition, transform var(--transition-fast));
  }

  :host([open]) .chevron {
    transform: rotate(180deg);
  }

  .loader {
    display: none;
    flex-shrink: 0;
    width: 1em;
    height: 1em;
    border: 2px solid currentcolor;
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: var(--_motion-animation, spin var(--transition-loader) infinite);
  }

  /* Keyframe must live in shadow DOM — @keyframes don't inherit across shadow roots */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  :host([loading]) .loader {
    display: inline-block;
  }

  /* ========================================
       Dropdown / Listbox
       ======================================== */

  .dropdown {
    position: fixed;
    inset: 0 auto auto 0;
    z-index: calc(var(--z-popover, 1000) + 1);
    box-sizing: border-box;
    max-height: min(16rem, 50dvh);
    padding: var(--size-1);
    margin: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    text-align: start;
    pointer-events: none;
    scrollbar-width: thin;
    background: var(--_dropdown-bg);
    border: var(--border) solid var(--_dropdown-border-color);
    border-radius: var(--_radius);
    box-shadow: var(--shadow-lg);
    opacity: 0;
    backdrop-filter: blur(var(--blur-md));
    transform: translateY(var(--overlay-enter-translate-y));
    transition: var(
      --_motion-transition,
      opacity var(--transition-fast),
      transform var(--transition-fast),
      display var(--transition-fast) allow-discrete
    );
  }

  .dropdown[data-open] {
    pointer-events: auto;
    opacity: 1;
    transform: translateY(0);

    @starting-style {
      opacity: 0;
      transform: translateY(var(--overlay-enter-translate-y));
    }
  }

  /* ========================================
       Option Items
       ======================================== */

  .option {
    display: flex;
    gap: var(--size-2);
    align-items: center;
    padding: var(--size-1-5) var(--size-2-5);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    cursor: pointer;
    border-radius: var(--rounded-sm);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  /* Truncate long labels so options always stay single-line (fixed virtualizer row height). */
  .option > span:not(.option-check, .option-icon) {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .option:hover:not([data-disabled]) {
    background: var(--_option-hover-bg);
  }

  .option[data-focused]:not([data-disabled]) {
    color: var(--_theme-base);
    background: var(--_option-focus-bg);
  }

  .option[data-selected],
  .option[aria-selected='true'] {
    font-weight: var(--font-medium);
    color: var(--_theme-base);
    background: var(--_option-selected-bg);
  }

  .option[data-selected][data-focused],
  .option[aria-selected='true'][data-focused] {
    background: var(--_option-selected-focus-bg);
  }

  .option[data-disabled] {
    color: var(--color-contrast-400);
    cursor: not-allowed;
    opacity: 0.6;
  }

  .option-check {
    display: inline-flex;
    flex-shrink: 0;
    margin-inline-start: auto;
    color: var(--_theme-base);
    opacity: 0;
    transition: opacity var(--transition-fast);
  }

  .option[data-selected] .option-check,
  .option[aria-selected='true'] .option-check {
    opacity: 1;
  }

  .no-results,
  .no-results-create,
  .dropdown-loading {
    padding: var(--size-3) var(--size-2-5);
    font-size: var(--text-sm);
    color: var(--color-contrast-400);
    text-align: center;
  }

  .no-results-create {
    display: flex;
    gap: var(--size-1-5);
    align-items: center;
    width: 100%;
    padding: var(--size-1-5) var(--size-2-5);
    font-size: var(--text-sm);
    color: var(--_theme-content, var(--color-primary-content));
    text-align: start;
    cursor: pointer;
    background: none;
    border: 0;
    border-radius: var(--rounded-sm);
  }

  .no-results-create:hover,
  .no-results-create[data-focused] {
    background: color-mix(in oklch, var(--_theme-base, var(--color-primary)) 10%, transparent);
  }

  /* ========================================
       Option Icon
       ======================================== */

  .option-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
  }

  /* ========================================
       Slotted option elements (hidden data nodes)
       ======================================== */

  ::slotted(ore-combobox-option) {
    display: none;
  }
}
